import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Ticket, RefreshCcw } from "lucide-react";

const STATUS_STYLES = {
  active: "border-success/40 text-success bg-success/5",
  expired: "border-destructive/40 text-destructive bg-destructive/5",
  inactive: "border-border text-muted-foreground bg-muted",
  scheduled: "border-gold/40 text-gold bg-gold/5",
  exhausted: "border-destructive/40 text-destructive bg-destructive/5",
};
const STATUS_LABEL = {
  active: "Ativo",
  expired: "Expirado",
  inactive: "Inativo",
  scheduled: "Programado",
  exhausted: "Uso esgotado",
};

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const iso = (d) => d.toISOString().slice(0, 10);
const randomCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const emptyCoupon = () => {
  const today = new Date();
  const in30 = new Date(); in30.setDate(today.getDate() + 30);
  return {
    code: randomCode(),
    discount_type: "percent",
    discount_value: 10,
    valid_from: iso(today),
    valid_until: iso(in30),
    max_uses: "",
    applicable_service_ids: [],
    active: true,
  };
};

export default function AdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/admin/coupons"), api.get("/admin/services")]);
      setCoupons(c.data || []);
      setServices((s.data || []).filter((x) => x.active));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing(emptyCoupon());
  const openEdit = (c) => setEditing({
    ...c,
    max_uses: c.max_uses ?? "",
    applicable_service_ids: c.applicable_service_ids || [],
  });

  const toggleService = (id) => {
    setEditing((prev) => {
      const has = prev.applicable_service_ids.includes(id);
      return {
        ...prev,
        applicable_service_ids: has ? prev.applicable_service_ids.filter((x) => x !== id) : [...prev.applicable_service_ids, id],
      };
    });
  };

  const save = async () => {
    if (!editing.code || !/^[A-Za-z0-9_-]{3,32}$/.test(editing.code)) {
      toast.error("Código deve ter 3-32 caracteres (letras, números, - e _).");
      return;
    }
    if (!editing.valid_from || !editing.valid_until) {
      toast.error("Preencha as datas de validade.");
      return;
    }
    if (editing.valid_until < editing.valid_from) {
      toast.error("Data final deve ser posterior à inicial.");
      return;
    }
    const value = Number(editing.discount_value);
    if (!value || value <= 0) {
      toast.error("Valor do desconto deve ser maior que zero.");
      return;
    }
    if (editing.discount_type === "percent" && value > 100) {
      toast.error("Desconto percentual não pode passar de 100%.");
      return;
    }
    const payload = {
      code: editing.code.toUpperCase(),
      discount_type: editing.discount_type,
      discount_value: value,
      valid_from: editing.valid_from,
      valid_until: editing.valid_until,
      applicable_service_ids: editing.applicable_service_ids,
      active: editing.active,
    };
    if (editing.max_uses !== "" && editing.max_uses !== null) {
      payload.max_uses = Number(editing.max_uses);
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/admin/coupons/${editing.id}`, payload);
        toast.success("Cupom atualizado.");
      } else {
        await api.post("/admin/coupons", payload);
        toast.success("Cupom criado.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir o cupom ${c.code}? Esta ação é permanente.`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("Cupom excluído.");
      await load();
    } catch (_) { toast.error("Erro ao excluir."); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      await load();
    } catch (_) { toast.error("Erro ao alternar status."); }
  };

  const svcName = useMemo(
    () => Object.fromEntries(services.map((s) => [s.id, s.name])),
    [services],
  );

  return (
    <div className="space-y-6" data-testid="admin-coupons-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2"><Ticket className="w-3.5 h-3.5" /> Marketing e retenção</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Cupons de desconto</h1>
          <p className="text-muted-foreground mt-2">Crie campanhas com validade, limite de uso e serviços aplicáveis.</p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-coupon-btn"
        >
          <Plus className="w-4 h-4" /> Novo cupom
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-4 py-3 overline text-muted-foreground">Código</th>
              <th className="px-4 py-3 overline text-muted-foreground">Desconto</th>
              <th className="px-4 py-3 overline text-muted-foreground">Validade</th>
              <th className="px-4 py-3 overline text-muted-foreground">Usos</th>
              <th className="px-4 py-3 overline text-muted-foreground">Serviços</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Carregando…</td></tr>
            ) : coupons.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Nenhum cupom cadastrado ainda.</td></tr>
            ) : coupons.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`coupon-row-${c.id}`}>
                <td className="px-4 py-3 font-mono font-semibold text-primary">{c.code}</td>
                <td className="px-4 py-3">
                  {c.discount_type === "percent" ? `${c.discount_value}%` : brl(c.discount_value)}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {c.valid_from} → {c.valid_until}
                </td>
                <td className="px-4 py-3 text-xs">
                  {c.uses_count}
                  {c.max_uses ? <span className="text-muted-foreground"> / {c.max_uses}</span> : <span className="text-muted-foreground"> (ilimitado)</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {c.applicable_service_ids && c.applicable_service_ids.length > 0
                    ? <span className="inline-flex flex-wrap gap-1">
                        {c.applicable_service_ids.slice(0, 2).map((sid) => (
                          <span key={sid} className="bg-muted px-1.5 py-0.5 rounded-sm">{svcName[sid] || sid}</span>
                        ))}
                        {c.applicable_service_ids.length > 2 && <span className="text-muted-foreground">+{c.applicable_service_ids.length - 2}</span>}
                      </span>
                    : <span className="text-muted-foreground">Todos</span>}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`overline px-2 py-0.5 rounded-sm border ${STATUS_STYLES[c.status] || STATUS_STYLES.inactive}`}
                    data-testid={`coupon-status-${c.id}`}
                  >
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => toggleActive(c)}
                      title={c.active ? "Desativar" : "Ativar"}
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors"
                      data-testid={`toggle-coupon-${c.id}`}
                    >
                      <RefreshCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors"
                      data-testid={`edit-coupon-${c.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(c)}
                      className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                      data-testid={`delete-coupon-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setEditing(null)} data-testid="coupon-modal">
          <div className="bg-card border border-border rounded-sm p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold mb-1">{editing.id ? "Editar cupom" : "Novo cupom"}</div>
                <h3 className="brand-wordmark uppercase text-xl font-bold text-primary">{editing.id ? editing.code : "Criar campanha"}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-muted rounded-sm" data-testid="close-coupon-modal"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="overline text-muted-foreground mb-1 block">Código do cupom</label>
                <div className="flex gap-2">
                  <input
                    value={editing.code}
                    onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                    className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono uppercase"
                    placeholder="Ex: BEMVINDO10"
                    data-testid="coupon-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, code: randomCode() })}
                    className="inline-flex items-center gap-1 rounded-sm border border-border text-xs font-semibold px-3 py-2 hover:bg-muted transition-colors"
                    data-testid="coupon-code-regen"
                  >
                    <RefreshCcw className="w-3 h-3" /> Sugerir
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Tipo de desconto</label>
                  <select
                    value={editing.discount_type}
                    onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="coupon-type-select"
                  >
                    <option value="percent">Percentual (%)</option>
                    <option value="fixed">Valor fixo (R$)</option>
                  </select>
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">
                    Valor {editing.discount_type === "percent" ? "(%)" : "(R$)"}
                  </label>
                  <input
                    type="number" min="0" step={editing.discount_type === "percent" ? "1" : "0.01"}
                    value={editing.discount_value}
                    onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="coupon-value-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Válido de</label>
                  <input
                    type="date" value={editing.valid_from}
                    onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="coupon-from-input"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Válido até</label>
                  <input
                    type="date" value={editing.valid_until}
                    onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="coupon-until-input"
                  />
                </div>
              </div>

              <div>
                <label className="overline text-muted-foreground mb-1 block">Limite de uso (opcional)</label>
                <input
                  type="number" min="1"
                  value={editing.max_uses}
                  onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })}
                  placeholder="Deixe em branco para uso ilimitado"
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  data-testid="coupon-max-uses-input"
                />
              </div>

              <div>
                <label className="overline text-muted-foreground mb-2 block">Serviços aplicáveis</label>
                <div className="text-xs text-muted-foreground mb-2">
                  {editing.applicable_service_ids.length === 0
                    ? "Aplicável a todos os serviços (nenhum selecionado)"
                    : `${editing.applicable_service_ids.length} serviço(s) selecionado(s)`}
                </div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {services.map((s) => {
                    const checked = editing.applicable_service_ids.includes(s.id);
                    return (
                      <button
                        key={s.id} type="button"
                        onClick={() => toggleService(s.id)}
                        className={`px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors ${checked ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
                        data-testid={`coupon-svc-toggle-${s.id}`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                  {services.length === 0 && <span className="text-xs text-muted-foreground">Nenhum serviço ativo.</span>}
                </div>
              </div>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="accent-primary w-4 h-4"
                  data-testid="coupon-active-checkbox"
                />
                <span className="text-sm">Cupom ativo (pacientes podem aplicar)</span>
              </label>

              <div className="flex gap-2 justify-end pt-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                  data-testid="cancel-coupon-btn"
                >
                  Cancelar
                </button>
                <button
                  onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
                  data-testid="save-coupon-btn"
                >
                  {saving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
